// What screens and the server share about agent chats: names, limits and the
// shapes of answers. No imports with side effects (screens read it as
// `@trameria/core/agent-chat-shared`).

// ---- names ------------------------------------------------------------------

/**
 * `phone`: a phone app paired with this PC (the offline edition's Hub). It
 * shows the Hub's screens and runs no agent, so it is never offered as a
 * chat's device.
 */
export const AGENT_DEVICE_KINDS = ["this_pc", "remote", "phone"] as const;
export type AgentDeviceKind = (typeof AGENT_DEVICE_KINDS)[number];

/**
 * The name the offline edition's device was made with before it was named
 * after the PC (and still is when the PC's name is unknown). Screens show it
 * in their own language; a start that knows the PC's name renames it.
 */
export const THIS_PC_NAME = "この PC";

/**
 * - message: a post (person or parent) or a reply (agent)
 * - notice: news from a child chat (started, ended, stopped, ...)
 * - answer: the person's (or a parent's) answer to a turn item
 * - signal: something the agent of this chat must look at (a step became current, a draft asked for)
 * - mark: a line in the chat that wakes nobody (stopped, compacted, rewound)
 */
export const AGENT_ENTRY_KINDS = [
  "message",
  "notice",
  "answer",
  "signal",
  "mark",
] as const;
export type AgentEntryKind = (typeof AGENT_ENTRY_KINDS)[number];

export const AGENT_ENTRY_AUTHORS = [
  "user",
  "agent",
  "parent",
  "system",
] as const;
export type AgentEntryAuthor = (typeof AGENT_ENTRY_AUTHORS)[number];

/** open: a reply being written; withdrawn: a post taken back before it was read. */
export const AGENT_ENTRY_STATES = ["open", "done", "withdrawn"] as const;
export type AgentEntryState = (typeof AGENT_ENTRY_STATES)[number];

export const AGENT_RUN_STATUSES = [
  "running",
  "ended",
  "stopped",
  "failed",
] as const;
export type AgentRunStatus = (typeof AGENT_RUN_STATUSES)[number];

export const AGENT_REQUEST_KINDS = [
  "cancel",
  "read_now",
  "withdraw",
  "compact",
  "rewind",
  "rewind_preview",
] as const;
export type AgentRequestKind = (typeof AGENT_REQUEST_KINDS)[number];

export const AGENT_REQUEST_STATUSES = [
  "pending",
  "done",
  "failed",
  "expired",
] as const;
export type AgentRequestStatus = (typeof AGENT_REQUEST_STATUSES)[number];

export const AGENT_TURN_STATES = ["open", "done", "withdrawn"] as const;
export type AgentTurnState = (typeof AGENT_TURN_STATES)[number];

/** here: the person answers in the holder's list; parent: the holder's agent decides first. */
export const AGENT_TURN_ROUTES = ["here", "parent"] as const;
export type AgentTurnRoute = (typeof AGENT_TURN_ROUTES)[number];

export const AGENT_UNDO_OPS = ["created", "updated"] as const;
export type AgentUndoOp = (typeof AGENT_UNDO_OPS)[number];

export type { AgentKind, OtherMcpMode } from "@trameria/shared-agent";

/** Kinds of notices from a child (data.notice of a `notice` entry). */
export const AGENT_NOTICE_KINDS = [
  "started",
  "ended",
  "stopped",
  "instructed",
  "read",
  "turn_added",
  "raised",
  "answered",
  "step_closed",
] as const;
export type AgentNoticeKind = (typeof AGENT_NOTICE_KINDS)[number];

/** Kinds of signals to a chat's own agent (data.signal of a `signal` entry). */
export const AGENT_SIGNAL_KINDS = [
  "step_ready",
  "wait_target",
  "turn_declined",
  "turn_slack_draft",
  "turn_withdrawn",
  "turn_awaiting",
] as const;
export type AgentSignalKind = (typeof AGENT_SIGNAL_KINDS)[number];

// ---- limits -----------------------------------------------------------------

export const AGENT_CHAT_KEY_RE = /^[0-9a-f]{12}$/;
export const AGENT_CHAT_TITLE_MAX = 200;
export const AGENT_POST_MAX = 100_000;
export const AGENT_POST_FILES_MAX = 10;
/** A chat is at most this deep, counting the top as 1. */
export const AGENT_CHILD_DEPTH_MAX = 3;
/** A chat waits on at most this many children at once. */
export const AGENT_CHILD_OPEN_MAX = 5;
export const AGENT_CHILD_INSTRUCTION_MAX = 8000;
/** Chats a person's runs may launch on their own (another Case's chat) in an hour. */
export const AGENT_STANDALONE_LAUNCH_MAX = 10;
export const AGENT_NOTICE_EXCERPT_CHARS = 600;
/** Open turn items per chat. */
export const AGENT_TURN_OPEN_MAX = 20;
export const AGENT_TURN_FILES_MAX = 10;
/** How long a request waits for the agent before it expires. */
export const AGENT_REQUEST_TTL_MS = 60_000;
/** A rewind (files and session) gets longer. */
export const AGENT_REWIND_TTL_MS = 120_000;
/** A history handed to a new device's first run (characters). */
export const AGENT_HISTORY_CHARS = 40_000;

// ---- shapes -----------------------------------------------------------------

type OtherMcpMode = import("@trameria/shared-agent").OtherMcpMode;

export interface AgentRunSpec {
  agentKind: string | null;
  model: string | null;
  effort: string | null;
  cwd: string | null;
  otherMcp: OtherMcpMode;
  revision: number;
}

/** One input a run read (the first, or one injected) and the CLI's message id for it. */
export interface RunInputRecord {
  kind: "initial" | "inject";
  throughSeq: number;
  entryIds: string[];
  uuid: string | null;
}

export interface AgentChatRef {
  id: string;
  key: string;
  title: string | null;
}

export interface AgentChatDTO {
  id: string;
  key: string;
  title: string | null;
  label: { id: string; name: string } | null;
  /** The workspace it belongs to (cloud's), if any. */
  workspaceId: string | null;
  deviceId: string | null;
  run: AgentRunSpec;
  role: "chat" | "secretary";
  parentChatId: string | null;
  launchedByParent: boolean;
  forkedFromEntryId: string | null;
  caseId: string | null;
  caseStepId: string | null;
  lastSeq: number;
  readSeq: number;
  handledSeq: number;
  /** Up to where the owner has seen it (entries after it are new to them). */
  ownerReadSeq: number;
  /** The run going on now, if any. */
  running: {
    runId: string;
    startedAt: string;
    live: Record<string, unknown> | null;
  } | null;
  /**
   * Waiting for its turn: a post that wakes the agent is not read yet and no
   * run goes on (the device is busy with another chat, or about to start).
   */
  queued: boolean;
  /** Open turn items the person answers in this chat. */
  openItems: number;
  costUsd: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentEntryFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  /** An image's size when it was measured at upload (the thumbnail's box). */
  width: number | null;
  height: number | null;
  /** SHA-256 of the content (hex); null for files from before hashes or not read. */
  hash?: string | null;
}

export interface AgentEntryDTO {
  id: string;
  chatId: string;
  seq: number;
  kind: AgentEntryKind;
  author: AgentEntryAuthor;
  body: string;
  state: AgentEntryState;
  wakes: boolean;
  runId: string | null;
  fromChatId: string | null;
  data: Record<string, unknown> | null;
  changes: unknown[] | null;
  files: AgentEntryFile[];
  /** The signed instruction it was written with (the agent checks it). */
  signed?: { instruction: unknown; content: unknown } | null;
  createdAt: string;
  editedAt: string | null;
}

export interface AgentRunDTO {
  id: string;
  chatId: string;
  deviceId: string | null;
  status: AgentRunStatus;
  fromSeq: number;
  throughSeq: number;
  sessionId: string | null;
  branchSessionId: string | null;
  agentKind: string | null;
  model: string | null;
  costUsd: number | null;
  usage: Record<string, unknown> | null;
  contextTokens: number | null;
  error: string | null;
  live: Record<string, unknown> | null;
  startedAt: string;
  endedAt: string | null;
  rewoundAt: string | null;
}

export interface AgentRequestDTO {
  id: string;
  chatId: string;
  runId: string | null;
  kind: AgentRequestKind;
  params: Record<string, unknown>;
  status: AgentRequestStatus;
  result: Record<string, unknown> | null;
  createdAt: string;
  expiresAt: string;
  doneAt: string | null;
}

export interface AgentTurnRevision {
  spec: Record<string, unknown>;
  /** conversation: after talking with the person; approved: the person allowed it; human: the person edited a draft. */
  cause: "conversation" | "approved" | "human";
  reason: string;
  revisedAt: string;
}

export interface AgentTurnProposal {
  spec: Record<string, unknown>;
  reason: string;
  proposedAt: string;
}

export interface AgentTurnSlackDraft {
  state: "requested" | "placed" | "failed";
  requestedAt: string;
  version: number;
  doneAt?: string;
  url?: string | null;
  error?: string | null;
}

/** queued / shown / seen while open; done / withdrawn when closed. */
export type AgentTurnStatus =
  | "queued"
  | "shown"
  | "seen"
  | "done"
  | "withdrawn";

/** GET /api/me/agents/turn/history: the settled items that reached the person. */
export interface AgentTurnHistoryQuery {
  /** Words, all of which are in the title, the answer or the closing note. */
  q?: string;
  /** A chat (the one that asked or the one that held it). */
  chat?: string;
  /** A Case (the item's, else its chat's). */
  case?: string;
  cursor?: string;
  limit?: number;
}

export interface AgentTurnHistoryItem {
  id: string;
  kind: string;
  spec: Record<string, unknown>;
  state: "done" | "withdrawn";
  closedBy: "human" | "agent" | "parent" | null;
  note: string | null;
  answer: Record<string, unknown> | null;
  /** The answer as written into the chat (null when none was). */
  answerText: string | null;
  closedAt: string;
  createdAt: string;
  /** The chat that asked (where its answer and its line are). */
  chat: { id: string; key: string; title: string | null };
  /** The chat that held it for the person, when not the one that asked. */
  holder: { id: string; key: string; title: string | null } | null;
  caseId: string | null;
  /** Where it sits in the asking chat: its answer, else the last entry before it closed. */
  seq: number | null;
}

export interface AgentTurnHistory {
  items: AgentTurnHistoryItem[];
  next: string | null;
  /** The chats and Cases to narrow to (first page only). */
  facets: {
    chats: { id: string; key: string; title: string | null }[];
    cases: { id: string; title: string }[];
  } | null;
}

export interface AgentTurnItemDTO {
  id: string;
  chatId: string;
  holderChatId: string;
  route: AgentTurnRoute;
  kind: string;
  spec: Record<string, unknown>;
  state: AgentTurnState;
  status: AgentTurnStatus;
  /** Place among the holder's open items (0 = shown first); null when closed. */
  place: number | null;
  answer: Record<string, unknown> | null;
  note: string | null;
  closedBy: "human" | "agent" | "parent" | null;
  answeredChatId: string | null;
  seenAt: string | null;
  closedAt: string | null;
  createdAt: string;
  revisions: AgentTurnRevision[];
  proposal: AgentTurnProposal | null;
  version: number;
  slackDraft: AgentTurnSlackDraft | null;
  caseId: string | null;
  /** Files attached to the answer. */
  files: AgentEntryFile[];
  /** The asker's files its spec shows; the item's UI may read only these. */
  specFiles: AgentEntryFile[];
}

export interface AgentDeviceDTO {
  id: string;
  name: string;
  kind: AgentDeviceKind;
  /**
   * Its host brings its agent up by itself: a new chat may be posted before
   * the agent has reported what it has, and waits for it.
   */
  acceptsBeforeReport: boolean;
  settings: Record<string, unknown>;
  settingsRevision: number;
  capabilities: Record<string, unknown>;
  capabilitiesAt: string | null;
  seenAt: string | null;
  /**
   * Whether its agent is connected now, as the host knows it when read. A
   * change is told live (`agent_device.presence`): read the device again.
   */
  connected: boolean;
  createdAt: string;
}

export interface AgentUndoResultDTO {
  undone: { kind: string; id: string }[];
  skipped: { kind: string; id: string }[];
  conflicts: { kind: string; id: string; reason: string }[];
}

export interface AgentChatListDTO {
  items: AgentChatDTO[];
  nextCursor: string | null;
  /**
   * How many chats are under each top chat of the page (all depths, side
   * chats left out, under the list's filter). Absent while the list is
   * narrowed to some of them (active, a typed word) and for a top chat with
   * none.
   */
  belowCounts?: Record<string, number>;
}

/**
 * A tick of a chat's outline: a post of the person, or a reply a side chat
 * branches off. No text: the heads are read when a tick's card opens
 * (`AgentOutlineHead`), from the entries already read when they are there.
 */
export interface AgentOutlineItem {
  seq: number;
  /** On a reply with a side chat: the reply's id and the side chat's. */
  entryId?: string;
  sideChatId?: string;
}

/** A chat's outline, as of `through` (the last seq of the chat then). */
export interface AgentOutline {
  items: AgentOutlineItem[];
  through: number;
}

/** The text of a tick's card. */
export interface AgentOutlineHead {
  /** The head of the post (of the reply, on a side chat's tick). */
  post: string;
  /** The head of the reply right after a post, if any. */
  reply: string | null;
}

/** The head of a text: at most `n` code points, trimmed. */
export function excerptOf(
  body: string,
  n = AGENT_NOTICE_EXCERPT_CHARS,
): string {
  return Array.from(body.trim()).slice(0, n).join("");
}
